import type { ExtractionLayoutField } from '../hooks/invoice/useInvoiceExtractionLayout';
import { isCanadianLocation } from './isCanadianLocation';

export type KeyIdentifierInvoiceField =
    | 'roNumber'
    | 'purchaseOrderNumber'
    | 'shopRoNumber'
    | 'invoiceNumber'
    | 'timestamp'
    | 'taxesTotal'
    | 'orderTotal';

/** Tax layout hints stay off for Canadian shops and split GST/HST + PST invoices. */
export function shouldRememberTaxExtractionHint(args: {
    nexTaxFormat?: string | null;
    locationState?: string | null;
}): boolean {
    if (args.nexTaxFormat === 'split') return false;
    return !isCanadianLocation(args.locationState);
}

export interface KeyIdentifierCorrectionCommit {
    invoiceField: KeyIdentifierInvoiceField;
    teachField: ExtractionLayoutField;
    fieldLabel: string;
    correctedValue: string;
}

export function teachFieldForKeyIdentifier(
    field: KeyIdentifierInvoiceField,
): ExtractionLayoutField | null {
    if (field === 'purchaseOrderNumber') return 'purchaseOrderNumber';
    if (field === 'invoiceNumber') return 'invoiceNumber';
    if (field === 'timestamp') return 'invoiceDate';
    if (field === 'roNumber' || field === 'shopRoNumber') return 'roNumber';
    if (field === 'taxesTotal') return 'taxesTotal';
    if (field === 'orderTotal') return 'orderTotal';
    return null;
}

/** API observation field — shop RO is stored separately from folder RO. */
export function observationFieldForKeyIdentifier(
    field: KeyIdentifierInvoiceField,
): ExtractionLayoutField | 'shopRoNumber' | null {
    if (field === 'shopRoNumber') return 'shopRoNumber';
    return teachFieldForKeyIdentifier(field);
}

export function formatKeyIdentifierDate(d: Date): string {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
}

function normalizeKeyIdentifierDateValue(value: unknown): string | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return formatKeyIdentifierDate(value);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) return formatKeyIdentifierDate(parsed);
        return trimmed;
    }
    return null;
}

export function valuesMatchForKeyIdentifierCorrection(
    field: KeyIdentifierInvoiceField,
    original: string | number | Date | undefined,
    current: unknown,
    options?: { moneyByMagnitude?: boolean },
): boolean {
    if (original === current) return true;
    if (field === 'timestamp') {
        const origNorm = normalizeKeyIdentifierDateValue(original);
        const currNorm = normalizeKeyIdentifierDateValue(current);
        if (origNorm && currNorm) return origNorm === currNorm;
    }
    if (original instanceof Date && current instanceof Date) {
        return original.getTime() === current.getTime();
    }
    if (typeof original === 'string' && typeof current === 'string') {
        return original.trim() === current.trim();
    }
    if (field === 'timestamp' && original instanceof Date && typeof current === 'string') {
        return formatKeyIdentifierDate(original) === current.trim();
    }
    if (field === 'timestamp' && original instanceof Date && current instanceof Date) {
        return original.toDateString() === current.toDateString();
    }
    const empty = (value: unknown) => value == null || value === '';
    if (empty(original) && empty(current)) return true;
    if (field === 'taxesTotal' || field === 'orderTotal') {
        const left = typeof original === 'number' ? original : Number(String(original ?? '').replace(/[$,]/g, ''));
        const right = typeof current === 'number' ? current : Number(String(current ?? '').replace(/[$,]/g, ''));
        if (Number.isFinite(left) && Number.isFinite(right)) {
            const leftCents = Math.round(left * 100);
            const rightCents = Math.round(right * 100);
            if (options?.moneyByMagnitude) {
                return Math.abs(leftCents) === Math.abs(rightCents);
            }
            return leftCents === rightCents;
        }
    }
    if (typeof original === 'number' && typeof current === 'string') {
        return String(original).trim() === current.trim();
    }
    return false;
}

/** A printed Key Identifier the user says was read incorrectly. */
export interface SubmittedIdentifierCorrectionRequest {
    sourceField: KeyIdentifierInvoiceField;
    fieldLabel: string;
    submittedValue: string;
}

/** Why a post-submit correction cannot become an AI hint, or null when it can. */
export function submittedIdentifierCorrectionError(args: {
    sourceField: KeyIdentifierInvoiceField;
    submittedValue: string;
    correctedValue: string;
    original: string | number | Date | undefined;
}): string | null {
    const corrected = args.correctedValue.trim();
    if (!corrected) return 'Enter the value printed on the invoice.';

    if (valuesMatchForKeyIdentifierCorrection(args.sourceField, args.submittedValue, corrected)) {
        return 'That matches the current value. Enter the value printed on the invoice.';
    }

    const teachField = teachFieldForKeyIdentifier(args.sourceField);
    if (!teachField) return 'This field cannot be saved as an AI hint.';
    const changed = keyIdentifierCorrectionChanged(
        {
            invoiceField: args.sourceField,
            teachField,
            fieldLabel: '',
            correctedValue: corrected,
        },
        args.original,
    );
    if (!changed) {
        return 'That matches what was extracted. Enter the value printed on the invoice.';
    }
    return null;
}

/** True when the committed correction differs from the original extraction snapshot. */
export function keyIdentifierCorrectionChanged(
    commit: KeyIdentifierCorrectionCommit,
    original: string | number | Date | undefined,
    options?: { moneyByMagnitude?: boolean },
): boolean {
    const corrected = commit.correctedValue.trim();
    if (!corrected) return false;
    return !valuesMatchForKeyIdentifierCorrection(
        commit.invoiceField,
        original,
        corrected,
        options,
    );
}
